
import "es6-promise";

import map from "lodash-es/map";
import filter from "lodash-es/filter";
import includes from "lodash-es/includes";
import * as pluralize from "pluralize";

import axios, { AxiosResponse } from "axios";
import Vue, { ComponentOptions } from "vue";
import Component from "vue-class-component";
import {
  Emit,
  Inject,
  Model,
  Prop,
  Provide,
  Watch,
} from "vue-property-decorator";
import VueMarkdown from "vue-markdown";

import {
  EMPTY_TREE_ROOT,
  FileListItem,
  fileListToTree,
  findPair,
  flattenTree,
  is_archive_url,
  isR1File,
  objListToTree,
  simplifyFastqName,
  TreeNode,
} from "../file-tree-util";

import { longestCommonSuffix } from "../prefix";
import { escapeRegExp, reverseString } from "../util";

import { ADD_SAMPLES } from "../store";

import { Snackbar } from "../snackbar";
import { LaxyFile, Sample } from "../model";

import ENAFileSelect from "./ENA/ENAFileSelect.vue";
import ENASearchAboutBox from "./ENA/ENASearchAboutBox.vue";
import RemoteFilesSelect from "./RemoteSelect/RemoteFilesSelect.vue";
import RemoteFileSelectAboutBox from "./RemoteSelect/RemoteFileSelectAboutBox.vue";
import CSVSampleListUpload from "./CSVSampleListUpload/CSVSampleListUpload.vue";
import CSVAboutBox from "./CSVSampleListUpload/CSVAboutBox.vue";
import { ILaxyFile } from "../types";

interface DbAccession {
  accession: string;
}

interface DataSource {
  type: string;
  text: string;
}

@Component({
  components: {
    "vue-markdown": VueMarkdown,
    ENASearchAboutBox,
    "ena-file-select": ENAFileSelect,
    RemoteFilesSelect,
    RemoteFileSelectAboutBox,
    CSVSampleListUpload,
    CSVAboutBox,
  },
})
export default class InputFilesForm extends Vue {
  @Prop({ type: String, default: "Input data" })
  titleText!: string;

  @Prop({ type: String, default: "ENA" })
  initialSelectedSource!: string;

  sources: DataSource[] = [
    { type: "ENA", text: "Public data from ENA or SRA" },
    { type: "URL", text: "Files on a web or FTP server (URL)" },
    { type: "NEXTCLOUD_PUBLIC", text: "Public Nextcloud/Owncloud Share" },
    { type: "CSV", text: "Sample list from CSV / Excel" },
    // {type: 'SFTP_UPLOAD', text: 'SFTP upload'},
  ];
  selected_source: string = "ENA";
  url_input: string = "";
  // cloudstor_link_password: string = "";
  user_email: string = "my.username@example.com";
  sftp_upload_host: string = "laxy.erc.monash.edu";
  sftp_upload_path: string = "";
  sftp_upload_credentials: string = "";
  dataset_name: string = "";
  password_valid_days: number = 2;
  password_expiry: Date = this.daysInFuture(2);
  dataset_name_invalid: boolean = false;

  created() {
    this.selected_source = this.initialSelectedSource;
  }

  @Watch("initialSelectedSource")
  onInitialSelectedSourceChanged(newVal: string) {
    this.selected_source = newVal;
  }

  submit() {
    // This form doesn't actually get submitted, this is to satisfy the template
  }

  email_username() {
    return this.user_email.split("@")[0];
  }

  email_domain() {
    return this.user_email.split("@")[1];
  }

  sftp_otp() {
    return Math.random().toString(36).substring(7);
  }

  generateSFTPUploadCredentials(): void {
    if (!this.dataset_name) {
      this.dataset_name_invalid = true;
    } else {
      let username = this.email_username();
      let domain = this.email_domain();
      let otp = this.sftp_otp();
      this.sftp_upload_path = `${this.dataset_name}`;
      this.sftp_upload_credentials = `${username}_${domain}:${otp}@${this.sftp_upload_host}/${this.sftp_upload_path}/`;

      this.$emit("stepDone");
    }
  }

  daysInFuture(days: number): Date {
    let now = new Date();
    now.setDate(now.getDate() + days);
    return now;
  }

  // TODO: We might want to use: https://validatejs.org/#validators-url instead
  isValidURL(url: string): boolean {
    const valid_protocols = ["http:", "https:", "ftp:"];
    const a = document.createElement("a");
    a.href = url;
    return (
      a.host != null &&
      a.host != window.location.host &&
      includes(valid_protocols, a.protocol)
    );
  }


  addToCart(selectedFiles: FileListItem[]) {
    // console.log(this.selectedFiles);
    const cart_samples: Sample[] = [];
    const added_files: FileListItem[] = [];

    const names: string[] = map(selectedFiles, (i) => {
      return simplifyFastqName(i.name);
    });
    const commonSuffix = longestCommonSuffix(names);

    for (let f of selectedFiles) {
      if (f.name === "..") {
        continue;
      }
      if (added_files.includes(f)) continue;
      const pair = findPair(f, selectedFiles);

      let sname = f.name;
      sname = simplifyFastqName(f.name);
      sname = sname.replace(commonSuffix, "");
      if (sname === "") sname = commonSuffix;

      // copy and drop 'type' prop (ILaxyFile doesn't want 'type')
      const _f = Object.assign({}, f) as FileListItem;
      delete _f.type;
      let sfiles: any = [{ R1: _f as ILaxyFile }];
      if (pair != null) {
        const _pair = Object.assign({}, pair) as FileListItem;
        delete _pair.type;
        
        // Validate R1/R2 order based on actual filenames, not processing order
        // This prevents misassignment when files are processed out of order
        if (isR1File(_f.name)) {
          // Current file is actually R1, pair is R2
          sfiles = [{ R1: _f as ILaxyFile, R2: _pair as ILaxyFile }];
        } else {
          // Current file is actually R2, pair is R1
          sfiles = [{ R1: _pair as ILaxyFile, R2: _f as ILaxyFile }];
        }
      }
      cart_samples.push({
        name: sname,
        files: sfiles,
        metadata: { condition: "" },
      } as Sample);

      added_files.push(f);
      if (pair != null) added_files.push(pair);
    }
    this.$store.commit(ADD_SAMPLES, cart_samples);
    let count = selectedFiles.length;
    Snackbar.flashMessage(
      `Added ${count} ${pluralize("file", count)} to cart.`
    );

    //this.remove(this.selectedFiles);
    //this.selectedFiles = [];
  }

  openDialog(refName: string) {
    // console.log('Opened: ' + refName);
    ((this.$refs as any)[refName] as any).open();
  }

  closeDialog(refName: string) {
    // console.log('Closed: ' + refName);
    ((this.$refs as any)[refName] as any).close();
  }

  @Watch("selected_source")
  onDataSourceChanged(newVal: string, oldVal: string) {
    this.$emit("dataSourceChanged");
  }

  @Watch("url_input", { immediate: true })
  onURLInputChanged(newVal: string, oldVal: string) {
    if (this.selected_source == "URL" && this.isValidURL(newVal)) {
      this.$emit("stepDone");
    } else {
      this.$emit("invalidData");
    }
  }
}
